import { useCallback, useEffect, useRef, useState } from "react";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";

const EXAMPLES = [
  {
    style: "Digital Art",
    prompt:
      "A lone astronaut standing inside a flooded cathedral, moonlight pouring through stained glass, deep cobalt and silver, cinematic and dreamlike",
  },
  {
    style: "Oil Painting",
    prompt:
      "A grand piano on a stormy shoreline as waves crash around it, dramatic clouds, rich indigo and burnt orange",
  },
  {
    style: "Cyberpunk",
    prompt:
      "A masked singer beneath a neon elevated train at midnight, rain-soaked pavement, electric blue and hot magenta",
  },
  {
    style: "Black & White",
    prompt:
      "A hand holding a shattered mirror with one eye reflected in every fragment, stark studio lighting, high contrast",
  },
  {
    style: "Abstract",
    prompt:
      "Sound waves transformed into liquid ribbons colliding in space, cobalt, crimson, and gold, energetic layered composition",
  },
  {
    style: "Anime",
    prompt:
      "A young musician alone on a rooftop at dusk, a giant cassette tape floating like the moon, glowing city below",
  },
  {
    style: "Retro",
    prompt:
      "A 1970s sports car on an endless desert highway beneath an oversized orange sun, grainy vintage print texture",
  },
  {
    style: "Watercolor",
    prompt:
      "A human face dissolving into migrating birds over a quiet blue lake, soft bleeding pigments, melancholic mood",
  },
  {
    style: "Digital Art",
    prompt:
      "A glass heart suspended above a dark ocean, glowing cracks casting light onto the waves, surreal cinematic atmosphere",
  },
  {
    style: "Oil Painting",
    prompt:
      "Two dancers moving through a field of red flowers during a thunderstorm, expressive brushwork, deep romantic colors",
  },
  {
    style: "Surrealism",
    prompt:
      "An empty recording studio overtaken by neon vines and holographic flowers, moody purple and teal lighting",
  },
  {
    style: "Fine Art Photography",
    prompt:
      "A solitary figure walking through dense fog beneath enormous suspended speakers, minimalist surreal photography",
  },
  {
    style: "Bauhaus",
    prompt:
      "Layered geometric forms folding inward like a collapsing rhythm, black, cream, and acid green, screen-print texture",
  },
  {
    style: "Storybook Illustration",
    prompt:
      "A train traveling across the night sky above a sleeping coastal town, stars reflecting in the ocean, luminous blue palette",
  },
  {
    style: "Synthwave",
    prompt:
      "A glamorous 1980s motel pool at night with a glowing jukebox at the water's edge, faded neon, film grain",
  },
  {
    style: "Botanical Illustration",
    prompt:
      "Wildflowers growing from an open vintage microphone, soft violet, teal, and gold washes on textured paper",
  },
  {
    style: "Double Exposure",
    prompt:
      "A city built inside a transparent human head, tiny lights glowing along the streets, dark background, introspective mood",
  },
  {
    style: "Baroque",
    prompt:
      "A burning crown resting on a velvet chair in an abandoned ballroom, dramatic chiaroscuro, deep red and black",
  },
  {
    style: "Graphic Novel",
    prompt:
      "A motorcycle rider crossing a bridge made of equalizer bars above a futuristic city, electric lime and violet",
  },
  {
    style: "Mixed-Media Collage",
    prompt:
      "A circular explosion of torn paper, paint, and metallic fragments arranged like a vinyl record, bold blue, orange, and white",
  },
].map((example, i) => ({
  ...example,
  image: `/marketing/artwork/artwork-${String(i + 1).padStart(2, "0")}.webp`,
}));

const ARROW_CLASS =
  "grid size-[38px] place-items-center rounded-full border border-[#CFD5E6] bg-transparent p-0 font-figtree text-[18px] font-extrabold leading-none text-blue-600 transition-[color,background,border-color,transform] duration-[180ms] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[rgba(58,70,238,0.24)] enabled:hover:border-blue-600 enabled:hover:bg-blue-600 enabled:hover:text-white disabled:border-[#DDE1EB] disabled:text-[#ADB3C5] disabled:opacity-[0.58] motion-safe:enabled:hover:-translate-y-px";

function scrollTarget(scroller: HTMLElement, direction: 1 | -1) {
  const current = scroller.scrollLeft;
  const width = scroller.clientWidth;
  const maxScroll = scroller.scrollWidth - width;
  const cards = Array.from(scroller.children) as HTMLElement[];

  if (direction > 0) {
    const next = cards.find(
      (card) =>
        card.offsetLeft > current + 2 &&
        card.offsetLeft + card.offsetWidth > current + width + 2,
    );
    return next ? Math.min(next.offsetLeft, maxScroll) : maxScroll;
  }

  const earlier = cards.filter((card) => card.offsetLeft < current - 2);
  const previous =
    earlier.find((card) => card.offsetLeft >= current - width - 2) ??
    earlier[earlier.length - 1];
  return previous ? previous.offsetLeft : 0;
}

export default function ArtworkGallery() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateControls = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    setAtStart(scroller.scrollLeft <= 2);
    setAtEnd(scroller.scrollLeft >= maxScroll - 2);
  }, []);

  useEffect(() => {
    updateControls();
    window.addEventListener("resize", updateControls);
    return () => window.removeEventListener("resize", updateControls);
  }, [updateControls]);

  const scrollGroup = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    scroller.scrollTo({
      left: Math.max(0, scrollTarget(scroller, direction)),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  return (
    <MarketingSection className="overflow-hidden bg-[#F6F7FB] pt-5 [container-type:inline-size] max-[680px]:pt-[76px]">
      <SectionKicker>Made in Lutely</SectionKicker>
      <SectionTitle>One tool. Endless possibilities.</SectionTitle>
      <SectionIntro>
        These covers were created in Lutely. Explore the styles and prompts
        behind them, then bring your own imagination.
      </SectionIntro>
      {/* Reaches the section's right edge so the last card is never clipped. */}
      <div
        ref={scrollerRef}
        onScroll={updateControls}
        className="relative mt-10 flex w-[min(calc(50%+50cqw+6vw),1440px)] snap-x snap-mandatory gap-5 overflow-x-auto overscroll-x-contain pb-6 pr-[6vw] pt-1 [scrollbar-color:#3042E2_#DDE1EC] [scrollbar-width:thin] max-[680px]:w-[calc(100%+20px)] max-[680px]:pr-5"
      >
        {EXAMPLES.map((example) => (
          <article
            key={example.image}
            className="flex-[0_0_290px] snap-start overflow-hidden rounded-[23px] border border-[#DFE3F1] bg-white max-[680px]:basis-[82vw]"
          >
            <img
              src={example.image}
              alt={`${example.style} cover art example`}
              loading="lazy"
              className="block aspect-square w-full bg-[linear-gradient(145deg,#4430D7,#FF4593)] object-cover"
            />
            <div className="p-5">
              <span className="inline-block rounded-full bg-[#EEF1FF] px-[9px] py-[6px] font-open-sans text-[12px] font-black uppercase leading-[1.55] tracking-[0.06em] text-blue-600">
                {example.style}
              </span>
              <p className="mb-0 mt-[15px] font-open-sans text-[14px] leading-[1.55] text-[#4E566B]">
                {example.prompt}
              </p>
            </div>
          </article>
        ))}
      </div>
      <div
        role="group"
        aria-label="Artwork gallery navigation"
        className="mt-4 flex justify-center gap-[10px]"
      >
        <button
          type="button"
          aria-label="Show previous artwork examples"
          disabled={atStart}
          onClick={() => scrollGroup(-1)}
          className={ARROW_CLASS}
        >
          ←
        </button>
        <button
          type="button"
          aria-label="Show next artwork examples"
          disabled={atEnd}
          onClick={() => scrollGroup(1)}
          className={ARROW_CLASS}
        >
          →
        </button>
      </div>
    </MarketingSection>
  );
}
